import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { inferVapcFromVideoSize, loadVapcFromUrl, type VapcInfo } from './parseVapc'

const VAP_ALPHA_FILTER_ID = 'vap-red-to-alpha-filter'

/** 将 VAP 右半灰度 R 通道映射为 alpha，无需 WebGL / CORS */
function ensureVapAlphaFilter() {
  if (document.getElementById(VAP_ALPHA_FILTER_ID)) return
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('aria-hidden', 'true')
  svg.style.position = 'absolute'
  svg.style.width = '0'
  svg.style.height = '0'
  svg.innerHTML = `<defs>
    <filter id="${VAP_ALPHA_FILTER_ID}" color-interpolation-filters="sRGB">
      <feColorMatrix type="matrix" values="
        0 0 0 0 0
        0 0 0 0 0
        0 0 0 0 0
        1 0 0 0 0" />
    </filter>
  </defs>`
  document.body.appendChild(svg)
}

function drawVapFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  maskCanvas: HTMLCanvasElement,
  info: VapcInfo,
): boolean {
  const ctx = canvas.getContext('2d')
  const maskCtx = maskCanvas.getContext('2d')
  if (!ctx || !maskCtx) return false

  const { w, h } = info
  if (canvas.width !== w) canvas.width = w
  if (canvas.height !== h) canvas.height = h
  if (maskCanvas.width !== w) maskCanvas.width = w
  if (maskCanvas.height !== h) maskCanvas.height = h

  const [rx, ry, rw, rh] = info.rgbFrame
  const [ax, ay, aw, ah] = info.aFrame

  ctx.clearRect(0, 0, w, h)
  ctx.globalCompositeOperation = 'source-over'
  ctx.filter = 'none'
  ctx.drawImage(video, rx, ry, rw, rh, 0, 0, w, h)

  maskCtx.clearRect(0, 0, w, h)
  maskCtx.globalCompositeOperation = 'source-over'
  maskCtx.filter = `url(#${VAP_ALPHA_FILTER_ID})`
  maskCtx.drawImage(video, ax, ay, aw, ah, 0, 0, w, h)
  maskCtx.filter = 'none'

  ctx.globalCompositeOperation = 'destination-in'
  ctx.drawImage(maskCanvas, 0, 0)
  ctx.globalCompositeOperation = 'source-over'
  return true
}

interface VapCanvasPlayerProps {
  src: string
  className?: string
  playing?: boolean
  onError?: () => void
  /** 方形预览区填充方式，Info 面板用 cover 避免白边 */
  objectFit?: 'contain' | 'cover'
}

/**
 * VAP mp4 播放器：Canvas 2D 按 rgbFrame/aFrame 合成透明动画。
 * 不 fetch mp4、不设 crossOrigin，避免 CDN 无 CORS 头时无法播放。
 */
export function VapCanvasPlayer({
  src,
  className,
  playing = true,
  onError,
  objectFit = 'contain',
}: VapCanvasPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const vapInfoRef = useRef<VapcInfo | null>(null)
  const rafRef = useRef<number | undefined>(undefined)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    ensureVapAlphaFilter()
    if (!maskCanvasRef.current) {
      maskCanvasRef.current = document.createElement('canvas')
    }
  }, [])

  useEffect(() => {
    vapInfoRef.current = null
    setReady(false)
  }, [src])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    let cancelled = false

    const handleLoaded = async () => {
      const fromVapc = await loadVapcFromUrl(src)
      if (cancelled) return
      const info =
        fromVapc ??
        inferVapcFromVideoSize(video.videoWidth, video.videoHeight)
      vapInfoRef.current = info
      if (!info) {
        onError?.()
        return
      }
      setReady(true)
    }

    const handleError = () => {
      onError?.()
    }

    video.addEventListener('loadedmetadata', handleLoaded)
    video.addEventListener('error', handleError)
    return () => {
      cancelled = true
      video.removeEventListener('loadedmetadata', handleLoaded)
      video.removeEventListener('error', handleError)
    }
  }, [src, onError])

  useEffect(() => {
    const video = videoRef.current
    const canvas = canvasRef.current
    const maskCanvas = maskCanvasRef.current
    if (!video || !canvas || !maskCanvas || !ready) return

    const draw = () => {
      const info = vapInfoRef.current
      if (!info || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        rafRef.current = requestAnimationFrame(draw)
        return
      }

      try {
        if (!drawVapFrame(video, canvas, maskCanvas, info)) {
          onError?.()
          return
        }
      } catch {
        onError?.()
        return
      }

      if (playing && !video.paused && !video.ended) {
        rafRef.current = requestAnimationFrame(draw)
      }
    }

    if (playing) {
      void video.play().catch(() => onError?.())
      rafRef.current = requestAnimationFrame(draw)
    } else {
      video.pause()
      draw()
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [playing, ready, onError])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = true
    video.loop = true
    video.playsInline = true
    video.preload = 'auto'
    // 只在 src 变化时加载；暂停时保留缓冲，避免滚回列表重复拉流
    video.src = src
    video.load()
  }, [src])

  return (
    <div
      ref={containerRef}
      className={cn('relative h-full w-full overflow-hidden', className)}
    >
      <video ref={videoRef} className="hidden" aria-hidden playsInline muted />
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 block h-full w-full"
        style={{ objectFit }}
      />
    </div>
  )
}
